/**
 * Service for handling Agent Chat API calls that require streaming or
 * multipart uploads (not suitable for RTK Query).
 *
 * Supports both text-only conversations and tool-enabled conversations
 * where the backend can request frontend tool execution (Tiptap AI Toolkit).
 *
 * Conversation CRUD has moved to copilotV2Api.js (RTK Query).
 */

import {getAiToolkit} from '@tiptap-pro/ai-toolkit';
import {Fragment} from '@tiptap/pm/model';
import store from '../store';
import {
  executeToolOnEditor,
  getSectionContents,
} from '../services/TiptapToolExecutor';
import {getSharedDocumentHeader} from './apiUtils';

const BASE_URL = `${process.env.REACT_APP_COPILOT_V2_API_URL}/api/v1`;
const LOG_PREFIX = '[agentChatService]';

let currentAbortController = null;
export const abortCurrentStream = () => {
  if (currentAbortController) {
    console.log(`${LOG_PREFIX} Aborting current stream`);
    currentAbortController.abort();
    currentAbortController = null;
  }
};

const getHeaders = (extra = {}) => {
  const {session} = store.getState();
  return {
    ...getSharedDocumentHeader(),
    ...(session.currentOrg?.orgId && {
      'x-inscope-org': session.currentOrg.orgId,
    }),
    ...(session.accessToken && {
      Authorization: `Bearer ${session.accessToken}`,
    }),
    ...extra,
  };
};

const fetchOk = async (url, options) => {
  const response = await fetch(url, options);
  if (!response.ok) {
    throw new Error(`HTTP error ${response.status}`);
  }
  return response;
};

const getFirstValidEditor = (editors) => {
  if (!editors) return null;
  const entry = Object.entries(editors).find(
    ([, editor]) => editor && !editor.isDestroyed
  );
  return entry ? entry[1] : null;
};

const getSchemaAwareness = (editors) => {
  const editor = getFirstValidEditor(editors);
  if (!editor) return '';
  try {
    const toolkit = getAiToolkit(editor);
    return toolkit.getHtmlSchemaAwareness?.() || '';
  } catch (error) {
    console.warn(`${LOG_PREFIX} Failed to get schema awareness:`, error);
    return '';
  }
};

const getSectionIds = (editors) => {
  if (!editors) return null;
  const ids = Object.keys(editors).filter(
    (id) => editors[id] && !editors[id].isDestroyed
  );
  return ids.length > 0 ? ids : null;
};

const getSectionMetadata = (sectionIds) => {
  if (!sectionIds?.length) return null;
  const sections = store.getState().documents?.document?.sections;
  if (!Array.isArray(sections) || !sections.length) return null;

  const idSet = new Set(sectionIds);
  const metadata = {};
  for (const section of sections) {
    if (idSet.has(section.id)) {
      metadata[section.id] = {name: section.title, slug: section.slug};
    }
  }
  return Object.keys(metadata).length > 0 ? metadata : null;
};

// Collect all nodes of a given type from a ProseMirror Fragment in tree order
const collectNodesByType = (fragment, typeName) => {
  const nodes = [];
  if (!fragment) return nodes;
  fragment.descendants((node) => {
    if (node.type.name === typeName) {
      nodes.push(node);
    }
  });
  return nodes;
};

// Replace linkedText nodes in a Fragment with their originals (preserving all attrs).
const replaceLinkedTextNodes = (fragment, originalNodes) => {
  let idx = 0;
  const mapNode = (node) => {
    if (node.type.name === 'linkedText' && idx < originalNodes.length) {
      return originalNodes[idx++];
    }
    if (node.content.size > 0) {
      const mappedChildren = [];
      node.content.forEach((child) => mappedChildren.push(mapNode(child)));
      return node.copy(Fragment.from(mappedChildren));
    }
    return node;
  };

  const topLevel = [];
  fragment.forEach((node) => topLevel.push(mapNode(node)));
  return Fragment.from(topLevel);
};

/**
 * beforeOperation hook for tiptapEdit tool calls.
 *  Rejects operations that insert document-table nodes.
 *  For replace operations with linked-text nodes:
 *    - Rejects if any linked-text `text` value was changed.
 *    - Patches the fragment with original linked-text nodes (preserving all attrs)
 *       when text values are unchanged
 */
const beforeOperationHook = ({operation, deleteContent, insertContent}) => {
  // reject document-table modifications
  if (collectNodesByType(insertContent, 'table').length > 0) {
    return {
      action: 'reject',
      error:
        'Cannot modify document-table nodes. Tables must be edited directly by the user.',
    };
  }

  // protect linked-text nodes
  const newLinkedTexts = collectNodesByType(insertContent, 'linkedText');
  const originalLinkedTexts = deleteContent
    ? collectNodesByType(deleteContent, 'linkedText')
    : [];

  // No linked-text in either side
  if (newLinkedTexts.length === 0 && originalLinkedTexts.length === 0) {
    return {action: 'accept'};
  }

  // Original had linked-text but new content doesn't
  if (originalLinkedTexts.length > 0 && newLinkedTexts.length === 0) {
    return {
      action: 'reject',
      error:
        'Cannot remove linked-text nodes. These are data-linked values from external sources.',
    };
  }

  // New content has linked-text but original doesn't (rare case)
  if (originalLinkedTexts.length === 0) {
    return {action: 'accept'};
  }

  // If counts differ, the AI removed or added linked-text nodes — reject.
  if (originalLinkedTexts.length !== newLinkedTexts.length) {
    return {
      action: 'reject',
      error:
        'Cannot add or remove linked-text nodes. These are data-linked values from external sources.',
    };
  }

  // Compare text values — reject if any changed.
  for (let i = 0; i < originalLinkedTexts.length; i++) {
    const originalText = String(originalLinkedTexts[i].attrs.text ?? '');
    const newText = String(newLinkedTexts[i].attrs.text ?? '');
    if (originalText !== newText) {
      return {
        action: 'reject',
        error:
          'Cannot modify linked-text values. These are data-linked values from external sources and must not be changed.',
      };
    }
  }

  // Text values match — update the fragment with original nodes to preserve all attrs
  const patchedFragment = replaceLinkedTextNodes(
    insertContent,
    originalLinkedTexts
  );

  return {action: 'accept', fragment: patchedFragment};
};

/**
 * Handle error events from the backend stream.
 * Returns a result object if processing should stop, or null to continue.
 */
const handleErrorEvent = (
  data,
  {messageId, completeResponse, onComplete, onError, isInternalCall}
) => {
  const errorCode = data.error_code || 'UNKNOWN_ERROR';

  if (errorCode === 'CLIENT_TOOL_CYCLES_EXCEEDED') {
    const userMessage =
      data.content ||
      "I've made multiple attempts but couldn't complete the task. Please be more specific with your request or break it into smaller steps.";

    if (onComplete && !isInternalCall) {
      onComplete({
        id: messageId,
        content: completeResponse || userMessage,
        done: true,
        toolsExecuted: false,
        error: errorCode,
      });
    }

    return {
      id: messageId,
      content: completeResponse || userMessage,
      toolsExecuted: false,
      error: errorCode,
    };
  }

  if (onError && !isInternalCall) {
    onError(new Error(data.content || 'Unknown error'));
  }
  return null;
};

/**
 * Handle text chunk events — update the streamed response.
 */
const handleChunkEvent = (
  data,
  {messageId, onChunk, isInternalCall, _prefixContent, _stableMessageId}
) => {
  if (!onChunk) return;

  if (!isInternalCall) {
    onChunk({id: messageId, content: data.content, done: false});
  } else if (_prefixContent !== undefined) {
    const displayContent = _prefixContent
      ? _prefixContent + '\n\n' + data.content
      : data.content;
    onChunk({
      id: _stableMessageId || messageId,
      content: displayContent,
      done: false,
    });
  }
};

/**
 * Handle a single tool_call event — execute the tool and collect results.
 */
const handleToolCallEvent = (
  data,
  {editors, toolExecutionResults, onToolCall}
) => {
  let docChanged = false;

  if (editors) {
    const toolCall = {
      tool_call_id: data.tool_call_id,
      tool_name: data.tool_name,
      input: data.input,
      parent_tool_use_id: data.parent_tool_use_id || null,
    };

    const result = executeToolOnEditor(editors, toolCall, {
      mode: 'preview',
      metadata: {type: 'penny-chat'},
      tiptapEditHooks: {beforeOperation: beforeOperationHook},
    });
    toolExecutionResults.push(result);
    if (result.docChanged) {
      docChanged = true;
    }
  }

  if (onToolCall) {
    onToolCall(data);
  }

  return docChanged;
};

/**
 * Handle tool_calls_pending — send results back and continue streaming.
 * Returns a result object if processing should stop, or null to continue.
 */
const handleToolCallsPending = async ({
  conversationId,
  options,
  editors,
  toolExecutionResults,
  messageId,
  completeResponse,
  isInternalCall,
  logPrefix,
  onToolExecuting,
  onToolComplete,
  onComplete,
  _prefixContent,
  _stableMessageId,
  docWasChanged,
  _priorThinkingBlocks,
}) => {
  if (!editors || toolExecutionResults.length === 0) {
    if (toolExecutionResults.length > 0) {
      console.warn(
        `${logPrefix} Tool results collected but no editors provided`
      );
    }
    return null;
  }

  if (onToolExecuting) {
    onToolExecuting();
  }

  if (onToolComplete && docWasChanged) {
    onToolComplete();
  }

  const continuationPrefix = _prefixContent
    ? _prefixContent + '\n\n' + completeResponse
    : completeResponse;

  const continuationResult = await sendToolResults(
    conversationId,
    toolExecutionResults,
    {
      ...options,
      _isInternalCall: true,
      _prefixContent: continuationPrefix,
      _stableMessageId: _stableMessageId || messageId,
      _priorThinkingBlocks: _priorThinkingBlocks || [],
    }
  );

  if (continuationResult.aborted) {
    return {
      id: messageId,
      content: completeResponse,
      toolsExecuted: true,
      aborted: true,
    };
  }

  const finalContent = continuationResult.content
    ? completeResponse
      ? completeResponse + '\n\n' + continuationResult.content
      : continuationResult.content
    : completeResponse;

  const finalMessageId = continuationResult.id || messageId;

  if (onComplete && !isInternalCall) {
    try {
      await onComplete({
        id: finalMessageId,
        content: finalContent,
        done: true,
        toolsExecuted: true,
      });
    } catch (err) {
      console.error(`${logPrefix} onComplete threw error:`, err);
    }
  }

  return {
    id: finalMessageId,
    content: finalContent,
    toolsExecuted: true,
  };
};

const updateLastBlock = (blocks, patch) => [
  ...blocks.slice(0, -1),
  {...blocks[blocks.length - 1], ...patch},
];

/**
 * Process a streaming SSE response from the backend.
 * Handles text chunks, tool execution, recursive tool calls, and errors.
 */
export const processStreamingResponse = async ({
  response,
  conversationId,
  options,
  isInternalCall = false,
  _priorThinkingBlocks = [],
  logPrefix = LOG_PREFIX,
}) => {
  const {
    callbacks = {},
    editors = null,
    _prefixContent,
    _stableMessageId,
    _signal,
  } = options;
  const {
    onChunk,
    onComplete,
    onToolCall,
    onToolExecuting,
    onToolComplete,
    onError,
    onThinkingChunk,
    onThinkingComplete,
  } = callbacks;

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let partialChunk = '';
  let completeResponse = '';
  // Initialize with any thinking blocks from prior stream phases (e.g., pre-tool reasoning)
  let thinkingBlocks = [..._priorThinkingBlocks];
  let messageId = null;
  let toolsExecuted = false;

  const toolExecutionResults = [];
  let docWasChanged = false;

  while (true) {
    if (_signal?.aborted) {
      reader.cancel();
      return {
        id: messageId,
        content: completeResponse,
        toolsExecuted,
        aborted: true,
      };
    }

    const {done, value} = await reader.read();
    if (done) break;

    const chunk = decoder.decode(value);
    const lines = (partialChunk + chunk).split('\n');
    partialChunk = lines.pop() || '';

    for (const line of lines) {
      if (!line.trim()) continue;

      try {
        const data = JSON.parse(line);

        if (!messageId && (data.id || data.message_id)) {
          messageId = data.id || data.message_id;
        }

        if (data.type === 'thinking_delta') {
          const lastBlock = thinkingBlocks[thinkingBlocks.length - 1];
          if (!lastBlock || lastBlock.isComplete) {
            thinkingBlocks = [
              ...thinkingBlocks,
              {content: data.content || '', isComplete: false},
            ];
          } else {
            thinkingBlocks = updateLastBlock(thinkingBlocks, {
              content: lastBlock.content + (data.content || ''),
            });
          }
          if (onThinkingChunk) {
            onThinkingChunk({id: messageId, thinkingBlocks});
          }
        } else if (data.type === 'thinking_complete') {
          const lastBlock = thinkingBlocks[thinkingBlocks.length - 1];
          if (lastBlock && !lastBlock.isComplete) {
            thinkingBlocks = updateLastBlock(thinkingBlocks, {
              isComplete: true,
            });
          }
          if (onThinkingComplete) {
            onThinkingComplete({id: messageId, thinkingBlocks});
          }
        } else if (data.type === 'error' || data.error_code) {
          // Error events (check first — they also have data.content)
          const result = handleErrorEvent(data, {
            messageId,
            completeResponse,
            onComplete,
            onError,
            isInternalCall,
          });
          if (result) return result;

          // Text chunks
        } else if (data.type === 'message_chunk' || data.content) {
          completeResponse = data.content;
          handleChunkEvent(data, {
            messageId,
            onChunk,
            isInternalCall,
            _prefixContent,
            _stableMessageId,
          });

          // Tool call — execute immediately
        } else if (data.type === 'tool_call') {
          const changed = handleToolCallEvent(data, {
            editors,
            toolExecutionResults,
            onToolCall,
          });
          if (changed) docWasChanged = true;

          // All tool calls received — send results back
        } else if (data.type === 'tool_calls_pending') {
          toolsExecuted = true;
          const result = await handleToolCallsPending({
            conversationId,
            options,
            editors,
            toolExecutionResults,
            messageId,
            completeResponse,
            isInternalCall,
            logPrefix,
            onToolExecuting,
            onToolComplete,
            onComplete,
            _prefixContent,
            _stableMessageId,
            docWasChanged,
            _priorThinkingBlocks: thinkingBlocks,
          });
          if (result) return result;

          // Stream complete
        } else if (data.type === 'message_complete' || data.done) {
          if (onComplete && !isInternalCall) {
            onComplete({
              id: messageId,
              content: completeResponse,
              done: true,
              toolsExecuted,
            });
          }
        } else {
          console.log(`${logPrefix} Unknown event:`, data);
        }
      } catch (e) {
        console.error(`${logPrefix} Error parsing SSE line:`, e, line);
      }
    }
  }

  return {id: messageId, content: completeResponse, toolsExecuted};
};

/**
 * Send a message and get a streaming response.
 * Supports tool-enabled conversations
 */
export const sendMessage = async (conversationId, message, options = {}) => {
  const {
    callbacks = {},
    attachments = null,
    editors = null,
    documentType = null,
  } = options;
  const {onError} = callbacks;

  abortCurrentStream();
  const abortController = new AbortController();
  currentAbortController = abortController;
  options = {...options, _signal: abortController.signal};

  try {
    const schemaAwareness = getSchemaAwareness(editors);
    const sectionIds = getSectionIds(editors);
    const sectionMetadata = getSectionMetadata(sectionIds);

    // Always send full document content so the LLM can skip tiptapRead
    // and edit directly using _hash attributes from the front-loaded context.
    const sectionContents = getSectionContents(editors);

    const requestBody = {
      message,
      ...(attachments && {attachments}),
      ...(documentType && {document_type: documentType}),
      ...(schemaAwareness && {schema_awareness: schemaAwareness}),
      ...(sectionIds && {section_ids: sectionIds}),
      ...(sectionContents && {section_contents: sectionContents}),
      ...(sectionMetadata && {
        message_metadata: {section_metadata: sectionMetadata},
      }),
    };

    const response = await fetch(
      `${BASE_URL}/conversations/${conversationId}/messages`,
      {
        method: 'POST',
        headers: getHeaders({'Content-Type': 'application/json'}),
        body: JSON.stringify(requestBody),
        signal: abortController.signal,
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error ${response.status}`);
    }

    const result = await processStreamingResponse({
      response,
      conversationId,
      options,
      isInternalCall: false,
      logPrefix: `${LOG_PREFIX} sendMessage`,
    });

    if (currentAbortController === abortController) {
      currentAbortController = null;
    }
    return result;
  } catch (error) {
    if (currentAbortController === abortController) {
      currentAbortController = null;
    }
    if (error.name === 'AbortError') {
      console.log(`${LOG_PREFIX} sendMessage aborted`);
      return {id: null, content: '', toolsExecuted: false, aborted: true};
    }
    console.error(`${LOG_PREFIX} sendMessage error:`, error);
    if (onError) onError(error);
    throw error;
  }
};

/**
 * Send tool execution results back to the backend.
 * Called automatically by processStreamingResponse on tool_calls_pending.
 */
export const sendToolResults = async (
  conversationId,
  toolResults,
  options = {}
) => {
  const {
    callbacks = {},
    _isInternalCall = false,
    editors,
    _signal = null,
  } = options;
  const {onError} = callbacks;

  try {
    const formattedResults = toolResults.map((tr) => ({
      tool_call_id: tr.tool_call_id,
      tool_name: tr.tool_name,
      output: tr.output,
      success: tr.success !== false,
      parent_tool_use_id: tr.parent_tool_use_id || null,
    }));

    const sectionIds = editors ? getSectionIds(editors) : null;
    const sectionContents = getSectionContents(editors);
    const sectionMetadata = getSectionMetadata(sectionIds);

    const requestBody = {
      tool_results: formattedResults,
      ...(sectionIds && {section_ids: sectionIds}),
      ...(sectionContents && {section_contents: sectionContents}),
      ...(sectionMetadata && {
        message_metadata: {section_metadata: sectionMetadata},
      }),
    };

    const response = await fetch(
      `${BASE_URL}/conversations/${conversationId}/messages`,
      {
        method: 'POST',
        headers: getHeaders({'Content-Type': 'application/json'}),
        body: JSON.stringify(requestBody),
        ...(_signal && {signal: _signal}),
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error ${response.status}`);
    }

    return await processStreamingResponse({
      response,
      conversationId,
      options,
      isInternalCall: _isInternalCall,
      _priorThinkingBlocks: options._priorThinkingBlocks || [],
      logPrefix: `${LOG_PREFIX} sendToolResults`,
    });
  } catch (error) {
    if (error.name === 'AbortError') {
      console.log(`${LOG_PREFIX} sendToolResults aborted`);
      return {id: null, content: '', toolsExecuted: false, aborted: true};
    }
    console.error(`${LOG_PREFIX} sendToolResults error:`, error);
    if (onError && !_isInternalCall) onError(error);
    throw error;
  }
};

/** Upload files for a conversation. */
export const uploadFiles = async (conversationId, files) => {
  const formData = new FormData();
  files.forEach((file) => formData.append('files', file));

  const response = await fetchOk(
    `${BASE_URL}/conversations/${conversationId}/upload`,
    {method: 'POST', headers: getHeaders(), body: formData}
  );
  return response.json();
};

/** Download an attachment from a message. */
export const downloadAttachment = async (
  conversationId,
  messageId,
  attachmentId
) => {
  const response = await fetchOk(
    `${BASE_URL}/conversations/${conversationId}/messages/${messageId}/attachments/${attachmentId}/download`,
    {method: 'GET', headers: getHeaders()}
  );
  const result = await response.json();
  return result.download_url;
};

export default {
  sendMessage,
  sendToolResults,
  uploadFiles,
  downloadAttachment,
  abortCurrentStream,
};
